/**
 * PricingPage — Operator Console super_admin.
 *
 * 2 tab:
 *  - Tier: prezzi monthly/yearly + trial_days per Free/Pro/Business
 *  - Capability: editor pitch + immagine upsell + value_pitch per capability
 *
 * Tutto scrive direttamente Firestore (super_admin ha write su tier_definitions
 * e mkt_catalog_*). La modale `UpgradeRequiredCard` / `UpgradeDialog` legge
 * questi dati live — nessun deploy necessario per cambi pricing/contenuto.
 *
 * @since 2026-05-18 (MOD-5 console pricing)
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Alert, CircularProgress, IconButton, Tooltip,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { collection, doc, getDoc, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { tokens } from '../theme';
import { TierPricingTab } from '../components/pricing/TierPricingTab';
import { CapabilityUpsellTab } from '../components/pricing/CapabilityUpsellTab';
import { StrumentiACanoneTab } from '../components/strumenti/StrumentiACanoneTab';
import { TettoAiGiornalieroTab } from '../components/strumenti/TettoAiGiornalieroTab';

export interface TierDefDoc {
  id: 'base' | 'pro' | 'business';
  label: string;
  tagline?: string;
  description?: string;
  price_per_seat_eur_monthly: number;
  price_per_seat_eur_yearly: number;
  trial_days: number;
  included_capabilities: string[];
  stripe_product_id?: string;
  stripe_price_monthly_id?: string;
  stripe_price_yearly_id?: string;
}

export interface CapabilityDoc {
  id: string;
  type: 'module' | 'feature' | 'setting';
  label: string;
  description?: string;
  value_pitch?: string;
  value_pitch_short?: string;
  upsell_image_url?: string | null;
  category?: string;
  status?: string;
  /**
   * I prodotti per cui la funzione ha senso (09/10/2026): codici come in `config.prodotti_disponibili` delle aziende
   * (FOTOVOLTAICO, INFISSI_PVC…). Vuoto = per tutte. Il server ricalcola da solo le aziende quando cambia
   * (`onPertinenzaCatalogo*`). Regola in `shared/capabilities/pertinenza.ts`.
   */
  prodotti_pertinenti?: string[];
}

type TabKey = 'tiers' | 'capabilities' | 'strumenti' | 'tetto_ai';

export const PricingPage: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('tiers');
  const [tiers, setTiers] = useState<TierDefDoc[]>([]);
  const [capabilities, setCapabilities] = useState<CapabilityDoc[]>([]);
  /** I prodotti che le aziende dichiarano di vendere: le scelte possibili per `prodotti_pertinenti`. */
  const [prodottiConosciuti, setProdottiConosciuti] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      // Tier definitions. Durante la finestra di migrazione 2026-05-19 accettiamo
      // sia 'base' (canonical) sia 'free' (legacy alias).
      const baseSnap = await getDoc(doc(db, 'tier_definitions', 'base'));
      const baseRef = baseSnap.exists() ? baseSnap : await getDoc(doc(db, 'tier_definitions', 'free'));
      const ts = await Promise.all([
        Promise.resolve(baseRef),
        getDoc(doc(db, 'tier_definitions', 'pro')),
        getDoc(doc(db, 'tier_definitions', 'business')),
      ]);
      const tierDocs: TierDefDoc[] = ts.map((d) => ({
        // Normalizza l'id: anche se il doc Firestore si chiama ancora 'free',
        // lo presentiamo come 'base' al resto dell'app.
        id: (d.id === 'free' ? 'base' : d.id) as TierDefDoc['id'],
        ...(d.data() as Omit<TierDefDoc, 'id'>),
      }));
      setTiers(tierDocs);

      // Capabilities — solo modules + features (settings non hanno value_pitch)
      const [mods, feats] = await Promise.all([
        getDocs(collection(db, 'mkt_catalog_modules')),
        getDocs(collection(db, 'mkt_catalog_features')),
      ]);
      const caps: CapabilityDoc[] = [
        ...mods.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<CapabilityDoc, 'id'>) })),
        ...feats.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<CapabilityDoc, 'id'>) })),
      ].sort((a, b) => a.label.localeCompare(b.label, 'it'));
      setCapabilities(caps);
      // Le scelte vengono dai DATI: i «Prodotti disponibili» di tutte le aziende, più quelli già usati nel catalogo.
      const aziende = await getDocs(collection(db, 'tenants'));
      const prodotti = new Set<string>();
      for (const a of aziende.docs) {
        const lista = (a.data().config as { prodotti_disponibili?: unknown } | undefined)?.prodotti_disponibili;
        if (Array.isArray(lista)) for (const p of lista) if (typeof p === 'string' && p) prodotti.add(p);
      }
      for (const c of caps) for (const p of c.prodotti_pertinenti ?? []) prodotti.add(p);
      setProdottiConosciuti([...prodotti].sort());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  // Map tierId → label per CapabilityUpsellTab
  const tierLabelMap = useMemo(() => {
    const m: Record<string, string> = {};
    for (const t of tiers) m[t.id] = t.label;
    return m;
  }, [tiers]);

  // Map capabilityId → tier in cui è inclusa (per badge nella lista capability)
  const capabilityToTier = useMemo(() => {
    const m: Record<string, string> = {};
    const order: TierDefDoc['id'][] = ['base', 'pro', 'business'];
    for (const tierId of order) {
      const t = tiers.find((x) => x.id === tierId);
      if (!t) continue;
      for (const capId of t.included_capabilities ?? []) {
        if (!m[capId]) m[capId] = tierId; // primo tier che la include
      }
    }
    return m;
  }, [tiers]);

  const handleTierUpdate = async (tierId: string, patch: Partial<TierDefDoc>) => {
    // Durante la finestra di migrazione 2026-05-19: se l'utente edita 'base'
    // ma il doc Firestore si chiama ancora 'free', scrivi su 'free'.
    let docId = tierId;
    if (tierId === 'base') {
      const baseSnap = await getDoc(doc(db, 'tier_definitions', 'base'));
      if (!baseSnap.exists) docId = 'free';
    }
    await updateDoc(doc(db, 'tier_definitions', docId), patch as Record<string, unknown>);
    setTiers((prev) => prev.map((t) => (t.id === tierId ? { ...t, ...patch } : t)));
  };

  const handleCapabilityUpdate = async (cap: CapabilityDoc, patch: Partial<CapabilityDoc>) => {
    const collName = cap.type === 'feature' ? 'mkt_catalog_features' : 'mkt_catalog_modules';
    await updateDoc(doc(db, collName, cap.id), patch as Record<string, unknown>);
    setCapabilities((prev) => prev.map((c) => (c.id === cap.id ? { ...c, ...patch } : c)));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography
            sx={{
              fontFamily: tokens.font.display,
              fontWeight: 600,
              fontSize: { xs: '1.6rem', md: '1.95rem' },
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              color: tokens.color.graphite,
              fontVariationSettings: '"WONK" 0, "SOFT" 30',
            }}
          >
            Pricing & Upsell
          </Typography>
          <Typography variant="body2" sx={{ color: tokens.color.ash, mt: 0.5, maxWidth: 600 }}>
            Editor tier commerciali e contenuti upsell (pitch, immagini) — tutto data-driven, propaga ovunque senza redeploy.
          </Typography>
        </Box>
        <Tooltip title="Ricarica">
          <IconButton onClick={reload} disabled={loading} size="small">
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined" sx={{ borderColor: tokens.color.line, bgcolor: tokens.color.paper }}>
        <Tabs
          value={tab}
          onChange={(_, v: TabKey) => setTab(v)}
          sx={{
            borderBottom: `1px solid ${tokens.color.line}`,
            '& .MuiTab-root': {
              fontFamily: tokens.font.body,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              minHeight: 48,
            },
          }}
        >
          <Tab value="tiers" label={`Tier (${tiers.length})`} />
          <Tab value="capabilities" label={`Capability upsell (${capabilities.length})`} />
          <Tab value="strumenti" label="Strumenti a canone" />
          <Tab value="tetto_ai" label="Tetto AI giornaliero" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {tab === 'strumenti' ? (
            <StrumentiACanoneTab />
          ) : tab === 'tetto_ai' ? (
            <TettoAiGiornalieroTab />
          ) : loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : tab === 'tiers' ? (
            <TierPricingTab tiers={tiers} onUpdate={handleTierUpdate} />
          ) : (
            <CapabilityUpsellTab
              capabilities={capabilities}
              capabilityToTier={capabilityToTier}
              tierLabelMap={tierLabelMap}
              prodottiConosciuti={prodottiConosciuti}
              onUpdate={handleCapabilityUpdate}
            />
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default PricingPage;
